import React, { useState, useEffect, useContext, useMemo, useCallback } from "react";
import "../components/TaskBoard.css";
import "./Task.css";
import DashboardLayout from "../layouts/DashboardLayout";
import AddTaskModal from "../components/AddTaskModal";
import TransferTaskModal from "../components/TransferTaskModal";
import EditTaskModal from "../components/EditTaskModal";
import { toast } from "react-toastify";
import TaskDetailsModal from "../components/TaskDetailsModal";
import BulkUploadModal from "../components/BulkUploadModal";
import { FaEye, FaSearch, FaRedo, FaFileExport, FaCloudUploadAlt, FaExchangeAlt, FaPlus, FaBuilding, FaProjectDiagram, FaUser, FaClock, FaFlag, FaEllipsisV, FaFilter } from "react-icons/fa";
import { NotificationsContext } from "../context/NotificationsContext";
import getApiUrl from "../utils/api";
import Select from "react-select";
import Pagination from "../components/Pagination";

const statusSummary = [
  { label: "All", color: "#7b6ef6" },
  { label: "Pending", color: "#a259e6" },
  { label: "Planned", color: "#4fc3f7" },
  { label: "WIP", color: "#ff9800" },
  { label: "Completed", color: "#4caf50" },
  { label: "Revalidate", color: "#f48fb1" },
  { label: "Delegated", color: "#ffb300" },
  { label: "Reopen", color: "#90a4ae" },
];

const priorityFilterOptions = [
  { value: "important", label: "Important" },
  { value: "high", label: "High" },
  { value: "medium", label: "Medium" },
  { value: "low", label: "Low" },
];

const overdueFilterOptions = [
  { value: "lt1d", label: "Under 1 day", minMinutes: 0, maxMinutes: 1440 },
  {
    value: "1to3d",
    label: "1 - 3 days",
    minMinutes: 1440,
    maxMinutes: 4320,
  },
  {
    value: "3to7d",
    label: "3 - 7 days",
    minMinutes: 4320,
    maxMinutes: 10080,
  },
  {
    value: "7to30d",
    label: "7 - 30 days",
    minMinutes: 10080,
    maxMinutes: 43200,
  },
  {
    value: "gt30d",
    label: "30+ days",
    minMinutes: 43200,
    maxMinutes: Number.POSITIVE_INFINITY,
  },
];

const formatFileSize = (bytes) => {
  if (!bytes || Number.isNaN(Number(bytes)) || Number(bytes) <= 0) {
    return "";
  }
  const units = ["B", "KB", "MB", "GB"];
  let value = Number(bytes);
  let index = 0;
  while (value >= 1024 && index < units.length - 1) {
    value /= 1024;
    index += 1;
  }
  const rounded = value >= 100 || value % 1 === 0 ? value.toFixed(0) : value.toFixed(1);
  return `${rounded} ${units[index]}`;
};

function normalizeToDate(value) {
  if (!value) return null;
  if (value instanceof Date) return new Date(value.getTime());
  if (typeof value === "number") return new Date(value);

  const raw = String(value || "").trim();
  if (!raw) return null;

  // Handle DD/MM/YYYY or DD-MM-YYYY patterns
  const dmyMatch = raw.match(/^(\d{1,2})[-/](\d{1,2})[-/](\d{4})/);
  if (dmyMatch) {
    const day = parseInt(dmyMatch[1], 10);
    const month = parseInt(dmyMatch[2], 10) - 1;
    const year = parseInt(dmyMatch[3], 10);

    // Check if there's a time part
    const timePart = raw.match(/[ \s](\d{1,2}):(\d{1,2})(?::(\d{1,2}))?/);
    const date = timePart
      ? new Date(year, month, day, parseInt(timePart[1], 10), parseInt(timePart[2] || 0, 10), parseInt(timePart[3] || 0, 10))
      : new Date(year, month, day);

    if (!timePart) {
      date.setHours(23, 59, 59, 999);
    }
    return isNaN(date.getTime()) ? null : date;
  }

  // Fallback to standard parser (handles YYYY-MM-DD)
  const date = new Date(raw.replace(" ", "T"));
  if (!isNaN(date.getTime())) {
    // If it was a date-only string (e.g. 2026-01-20), set to EOD
    if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) {
      date.setHours(23, 59, 59, 999);
    }
    return date;
  }

  return null;
}

const resolveTaskTargetDate = (task) =>
  task?.target_date ||
  task?.targetDate ||
  task?.target_end_date ||
  task?.targetEndDate ||
  task?.due_date ||
  task?.dueDate ||
  task?.end_date ||
  task?.endDate ||
  null;

const resolveTaskStartDate = (task) =>
  task?.start_date ||
  task?.startDate ||
  task?.created_at ||
  task?.createdAt ||
  null;

function resolveTaskCompletionDate(task) {
  if (!task) return null;

  const normalizedStatus = (task?.status || task?.statusForCurrentUser || task?.originalStatus || "")
    .toString()
    .trim()
    .toLowerCase();


  const candidates = [
    task?.completed_at,
    task?.completedAt,
    task?.completion_at,
    task?.completionAt,
    task?.completed_on,
    task?.completedOn,
    task?.status_completed_at,
    task?.statusCompletedAt,
    task?.last_completed_at,
    task?.lastCompletedAt,
    task?.overdueCompletionAt,
    task?.overdue_completion_at,
  ];

  for (const value of candidates) {
    const parsed = normalizeToDate(value);
    if (parsed) {
      return parsed;
    }
  }

  if (typeof task?.overdueCompletionTimestamp === "number" && !Number.isNaN(task.overdueCompletionTimestamp)) {
    const timestampDate = normalizeToDate(task.overdueCompletionTimestamp);
    if (timestampDate) {
      return timestampDate;
    }
  }

  const isCompleted = normalizedStatus === "completed" || normalizedStatus === "complete" || normalizedStatus === "done";

  if (isCompleted) {
    const fallback = [
      task?.status_updated_at,
      task?.statusUpdatedAt,
      task?.completed_timestamp,
      task?.completedTimestamp,
      task?.updated_at,
      task?.updatedAt,
      task?.completed_at,
      task?.completedAt,
      task?.status_completed_at,
      task?.statusCompletedAt,
    ];

    for (const value of fallback) {
      const parsed = normalizeToDate(value);
      if (parsed) {
        return parsed;
      }
    }
  }

  return null;
}

function resolveTaskCompletionTimestamp(task) {
  if (!task) return null;

  const status = (task?.status || task?.statusForCurrentUser || "").toString().trim().toLowerCase();
  const isCompleted = status === "completed" || status === "complete" || status === "done";

  if (typeof task.overdueCompletionTimestamp === "number" && !Number.isNaN(task.overdueCompletionTimestamp)) {
    return task.overdueCompletionTimestamp;
  }

  // If it's a group entry, use the aggregated ageingCompletionTimestamp ONLY if status is fully completed
  if (typeof task.ageingCompletionTimestamp === "number" && !Number.isNaN(task.ageingCompletionTimestamp)) {
    if (isCompleted) {
      return task.ageingCompletionTimestamp;
    }
    // If status is "Mixed" or something else, we don't cap aging yet
    return null;
  }

  const parsed = normalizeToDate(task?.overdueCompletionTimestamp);
  if (parsed) {
    return parsed.getTime();
  }

  const completionDate = resolveTaskCompletionDate(task);
  return completionDate ? completionDate.getTime() : null;
}

const statusPriorityOrder = [
  "pending",
  "planned",
  "wip",
  "hold",
  "revalidate",
  "delegated",
  "reopen",
  "other",
];

const badgeClassLookup = {
  pending: "Pending",
  planned: "Planned",
  wip: "WIP",
  hold: "Pending",
  revalidate: "Revalidate",
  delegated: "Delegated",
  reopen: "Reopen",
  completed: "Completed",
};

function statusPriorityIndex(status) {
  const idx = statusPriorityOrder.indexOf(status);
  return idx === -1 ? statusPriorityOrder.length : idx;
}

function resolveAggregatedDisplayStatus(task, resolveStatusLabel) {
  const fallbackLabel = resolveStatusLabel(task?.status);
  const fallbackNormalized = fallbackLabel.toLowerCase();

  if (
    !task?.statusBreakdown ||
    typeof task.statusBreakdown !== "object" ||
    Object.keys(task.statusBreakdown).length === 0
  ) {
    return {
      label: fallbackLabel,
      normalized: fallbackNormalized,
    };
  }

  const entries = Object.entries(task.statusBreakdown)
    .map(([status, value]) => {
      const label = resolveStatusLabel(status);
      const normalized = (status || "")
        .toString()
        .trim()
        .toLowerCase();
      return {
        label,
        normalized,
        value: Number(value) || 0,
      };
    })
    .filter((entry) => entry.value > 0);

  if (entries.length === 0) {
    return {
      label: fallbackLabel,
      normalized: fallbackNormalized,
    };
  }

  const totalAssignees = entries.reduce((sum, entry) => sum + entry.value, 0);
  const distinctStatuses = new Set(entries.map((entry) => entry.normalized));

  if (distinctStatuses.size === 1) {
    const [onlyEntry] = entries;
    if (onlyEntry.normalized === "completed") {
      return { label: "Completed", normalized: "completed" };
    }
    return {
      label: onlyEntry.label,
      normalized: onlyEntry.normalized || fallbackNormalized,
    };
  }

  const outstanding = entries.filter(
    (entry) => entry.normalized !== "completed"
  );

  if (outstanding.length === 0) {
    return { label: "Completed", normalized: "completed" };
  }

  outstanding.sort(
    (a, b) => statusPriorityIndex(a.normalized) - statusPriorityIndex(b.normalized)
  );

  const outstandingTotal = outstanding.reduce(
    (sum, entry) => sum + entry.value,
    0
  );

  const primary = outstanding[0];
  const label =
    outstanding.length > 1
      ? `${primary.label} (${outstandingTotal}/${totalAssignees})`
      : primary.label;

  return {
    label,
    normalized: primary.normalized || fallbackNormalized,
  };
}

function formatStatusBreakdownTooltip(task, resolveStatusLabel) {
  if (
    !task?.statusBreakdown ||
    typeof task.statusBreakdown !== "object" ||
    Object.keys(task.statusBreakdown).length === 0
  ) {
    return "";
  }

  const parts = Object.entries(task.statusBreakdown)
    .map(([status, count]) => {
      const label = resolveStatusLabel(status);
      return `${label}: ${count}`;
    })
    .filter(Boolean);

  return parts.join(", ");
}

function calculateDelayMs(targetDateValue, cutoffDateValue = null) {
  const anchor = normalizeToDate(targetDateValue);
  if (!anchor) return null;

  let comparisonTimestamp;
  if (cutoffDateValue !== null && cutoffDateValue !== undefined) {
    const cutoffDate = normalizeToDate(cutoffDateValue);
    if (!cutoffDate) return null; // If task is completed but we can't find valid date, don't show wrong value
    comparisonTimestamp = cutoffDate.getTime();
  } else {
    comparisonTimestamp = Date.now();
  }

  const diff = comparisonTimestamp - anchor.getTime();
  return diff <= 0 ? 0 : diff;
}

function calculateOverdueMinutes(targetDateValue, cutoffDateValue = null) {
  const delayMs = calculateDelayMs(targetDateValue, cutoffDateValue);
  if (delayMs === null) return null;
  return Math.floor(delayMs / 60000);
}

function calculateOverdueDisplay(targetDateValue, cutoffDateValue = null) {
  console.log("targetDateValue", targetDateValue);
  console.log("cutoffDateValue", cutoffDateValue);
  const delayMs = calculateDelayMs(targetDateValue, cutoffDateValue);
  if (delayMs === null) return "";
  if (delayMs <= 0) return "0d";

  const seconds = Math.floor(delayMs / 1000);
  if (seconds < 60) return `${seconds}s`;

  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m`;

  const hours = Math.floor(minutes / 60);
  if (hours < 24) {
    const remM = minutes % 60;
    return remM > 0 ? `${hours}h ${remM}m` : `${hours}h`;
  }

  const days = Math.floor(hours / 24);
  if (days < 30) {
    const remH = hours % 24;
    return remH > 0 ? `${days}d ${remH}h` : `${days}d`;
  }

  const months = Math.floor(days / 30);
  if (months < 12) {
    const remD = days % 30;
    return remD > 0 ? `${months}mo ${remD}d` : `${months}mo`;
  }

  const years = Math.floor(months / 12);
  const remMo = months % 12;
  return remMo > 0 ? `${years}y ${remMo}mo` : `${years}y`;
}

function formatDate(dateStr) {
  if (!dateStr) return "";
  const date = new Date(dateStr);
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

// Combine tasks that share a group_id so multi-assignee entries collapse into one row.
function aggregateTasksByGroup(tasks) {
  if (!Array.isArray(tasks) || tasks.length === 0) {
    return [];
  }

  const groupMap = new Map();
  const order = [];

  tasks.forEach((task) => {
    const fallbackId = task?.id ?? `temp-${Math.random().toString(36).slice(2)}`;
    const hasGroup = task?.group_id !== null && task?.group_id !== undefined;
    const key = hasGroup ? `group-${task.group_id}` : `task-${fallbackId}`;

    if (!groupMap.has(key)) {
      groupMap.set(key, {
        ...task,
        primaryTaskId: task?.id ?? null,
        assignees: [],
        allTaskIds: [],
        start_date: task?.start_date || null,
        overdueCompletionTimestamp: null,
      });
      order.push(key);
    }

    const entry = groupMap.get(key);
    const completionDate = resolveTaskCompletionDate(task);
    const completionTimestamp = completionDate ? completionDate.getTime() : null;
    const fullName = `${task?.FirstName || ""} ${task?.LastName || ""}`
      .replace(/\s+/g, " ")
      .trim();

    entry.assignees.push({
      taskId: task?.id ?? null,
      userId: task?.assigned_to ?? null,
      name: fullName || "Unassigned",
      email: task?.email || "",
      status: task?.status || "",
      priority: task?.priority || "",
      due_date: task?.due_date || null,
      created_at: task?.created_at || null,
      start_date: task?.start_date || null,
      overdueCompletionTimestamp: completionTimestamp,
    });

    entry.allTaskIds.push(task?.id ?? null);

    if (completionTimestamp !== null) {
      entry.overdueCompletionTimestamp = entry.overdueCompletionTimestamp !== null
        ? Math.max(entry.overdueCompletionTimestamp, completionTimestamp)
        : completionTimestamp;
    }

    if (
      !entry.created_at ||
      (task?.created_at &&
        new Date(task.created_at) < new Date(entry.created_at))
    ) {
      entry.created_at = task?.created_at || entry.created_at;
    }

    if (
      task?.due_date &&
      (!entry.due_date || new Date(task.due_date) > new Date(entry.due_date))
    ) {
      entry.due_date = task.due_date;
    }

    if (task?.start_date) {
      if (!entry.start_date) {
        entry.start_date = task.start_date;
      } else {
        const taskStart = normalizeToDate(task.start_date);
        const entryStart = normalizeToDate(entry.start_date);
        if (taskStart && entryStart && taskStart < entryStart) {
          entry.start_date = task.start_date;
        }
      }
    }

    if (!entry.priority) {
      entry.priority = task?.priority || entry.priority;
    }
  });

  const aggregated = order.map((key) => {
    const entry = groupMap.get(key);
    const assignees = entry.assignees || [];
    const total = assignees.length;
    const uniqueNames = Array.from(
      new Set(assignees.map((a) => a.name).filter(Boolean))
    );
    const uniqueEmails = Array.from(
      new Set(assignees.map((a) => a.email).filter(Boolean))
    );
    const statusBreakdown = assignees.reduce((acc, assignee) => {
      const label = assignee.status || "Pending";
      acc[label] = (acc[label] || 0) + 1;
      return acc;
    }, {});
    const statusSet = new Set(
      assignees
        .map((a) => (a.status || "").trim().toLowerCase())
        .filter(Boolean)
    );
    const aggregatedStatus =
      statusSet.size <= 1
        ? assignees[0]?.status || entry.status || "Pending"
        : "Mixed";

    const summaryName =
      total > 1
        ? `${uniqueNames[0] || "Multiple assignees"} +${total - 1} more`
        : uniqueNames[0] || "Unassigned";

    const emailPreview = (() => {
      if (total <= 1) {
        return uniqueEmails[0] || "";
      }
      const previewList = uniqueEmails.filter(Boolean).slice(0, 2);
      const remaining = uniqueEmails.length - previewList.length;
      if (remaining > 0) {
        previewList.push(`+${remaining} more`);
      }
      return previewList.join(", ");
    })();

    const firstName =
      total === 1
        ? uniqueNames[0]?.split(/\s+/)[0] || uniqueNames[0] || "Unassigned"
        : summaryName;
    const lastName =
      total === 1
        ? uniqueNames[0]?.split(/\s+/).slice(1).join(" ")
        : "";

    const completionTimestamp = assignees.reduce((max, assignee) => {
      const candidate = assignee?.overdueCompletionTimestamp;
      if (typeof candidate === "number" && !Number.isNaN(candidate)) {
        return max === null ? candidate : Math.max(max, candidate);
      }
      return max;
    }, entry.overdueCompletionTimestamp ?? null);

    return {
      ...entry,
      id: entry.primaryTaskId ?? entry.id,
      representativeTaskId: entry.primaryTaskId ?? entry.id,
      hasMultipleAssignees: total > 1,
      status: aggregatedStatus,
      statusBreakdown,
      FirstName: firstName,
      LastName: lastName,
      email: emailPreview,
      displayGivenToName:
        total === 1 ? uniqueNames[0] || "Unassigned" : summaryName,
      displayGivenToEmail: emailPreview,
      overdueCompletionTimestamp: completionTimestamp,
    };
  });

  return aggregated;
}



const Task = () => {
  const [showAddTask, setShowAddTask] = useState(false);
  const [showTransferTask, setShowTransferTask] = useState(false);
  const [showBulkUpload, setShowBulkUpload] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const lastPopupAtRef = React.useRef(0);
  const showSuccessOnce = (msg) => {
    const now = Date.now();
    if (now - lastPopupAtRef.current < 3000) return; // Increased to 3 seconds
    lastPopupAtRef.current = now;
    setSuccessMessage(msg);
    setShowSuccess(true);
  };
  const [tasks, setTasks] = useState([]);
  const [selectedTask, setSelectedTask] = useState(null);
  const [showDetails, setShowDetails] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState("All");
  const [showActionModal, setShowActionModal] = useState(false);
  const [actionTask, setActionTask] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editTask, setEditTask] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [userId, setUserId] = useState(null);
  const [departments, setDepartments] = useState([]);
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState("");
  const [allEmployees, setAllEmployees] = useState([]);
  const [selectedDepartment, setSelectedDepartment] = useState("");
  const [selectedEmployee, setSelectedEmployee] = useState("");
  const [selectedOverdueRange, setSelectedOverdueRange] = useState("");
  const [selectedPriority, setSelectedPriority] = useState("");
  const [filteredEmployees, setFilteredEmployees] = useState([]);
  const [filteredTasks, setFilteredTasks] = useState([]);
  const [showExportModal, setShowExportModal] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  // const [debouncedSearchQuery, setDebouncedSearchQuery] = useState(""); // Removed debounce
  const [isTaskReloading, setIsTaskReloading] = useState(false);
  const [selectedTasks, setSelectedTasks] = useState([]);
  const [statusCounts, setStatusCounts] = useState({ All: 0 });

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageLimit, setPageLimit] = useState(50);
  const [totalTasks, setTotalTasks] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  // Applied filters state for "Apply Filter" button functionality
  const [appliedFilters, setAppliedFilters] = useState({
    department: "",
    project: "",
    employee: "",
    priority: "",
    status: "All",
    overdue: "",
    search: ""
  });

  const handleApplyFilters = () => {
    setAppliedFilters({
      department: selectedDepartment,
      project: selectedProject,
      employee: selectedEmployee,
      priority: selectedPriority,
      status: selectedStatus,
      overdue: selectedOverdueRange,
      search: searchQuery
    });
    setCurrentPage(1);
  };

  // Debounce search query
  // Debounce search query - REMOVED
  /*
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
      setCurrentPage(1); // Reset to first page on search
    }, 500);
    return () => clearTimeout(timer);
  }, [searchQuery]);
  */

  // React-select options
  const departmentOptions = useMemo(() => [
    { value: "", label: "All Departments" },
    ...departments.map(dept => ({ value: dept.id, label: dept.name }))
  ], [departments]);

  const projectOptions = useMemo(() => [
    { value: "", label: "All Projects" },
    ...projects.map(proj => ({ value: proj.id, label: proj.name }))
  ], [projects]);

  const employeeOptions = useMemo(() => [
    { value: "", label: "All Employees" },
    ...filteredEmployees.map(emp => ({
      value: emp.id,
      label: `${emp.FirstName} ${emp.LastName}`
    }))
  ], [filteredEmployees]);

  const overdueOptions = useMemo(() => [
    { value: "", label: "Over Due (All)" },
    ...overdueFilterOptions.map(opt => ({ value: opt.value, label: opt.label }))
  ], []);

  const priorityOptions = useMemo(() => [
    { value: "", label: "Priority (All)" },
    ...priorityFilterOptions.map(opt => ({ value: opt.value, label: opt.label }))
  ], []);

  const reactSelectStyles = {
    control: (base, state) => ({
      ...base,
      paddingLeft: "30px", // space for icon
      borderRadius: "12px",
      border: state.isFocused ? "1.5px solid #e30613" : "1px solid #e2e8f0",
      backgroundColor: "#ffffff",
      fontSize: "0.9rem",
      color: "#334155",
      fontWeight: "500",
      height: "38px",
      minHeight: "38px",
      boxShadow: "none",
      transition: "all 0.2s ease",
      cursor: "pointer",
      "&:hover": {
        borderColor: "#cbd5e1",
      }
    }),
    valueContainer: (base) => ({
      ...base,
      padding: "0 8px",
    }),
    singleValue: (base) => ({
      ...base,
      color: "#334155",
    }),
    placeholder: (base) => ({
      ...base,
      color: "#94a3b8",
    }),
    indicatorSeparator: () => ({
      display: "none",
    }),
    dropdownIndicator: (base) => ({
      ...base,
      color: "#6b7280",
      padding: "4px 8px",
    }),
    menu: (base) => ({
      ...base,
      borderRadius: "12px",
      boxShadow: "0 10px 25px -5px rgba(0, 0, 0, 0.1)",
      overflow: "hidden",
      zIndex: 9999,
    }),
    menuPortal: (base) => ({
      ...base,
      zIndex: 9999
    }),
    option: (base, state) => ({
      ...base,
      fontSize: "0.9rem",
      padding: "10px 16px",
      backgroundColor: state.isSelected
        ? "#e30613"
        : state.isFocused
          ? "#fdeaea"
          : "transparent",
      color: state.isSelected ? "#ffffff" : "#334155",
      cursor: "pointer",
      "&:active": {
        backgroundColor: "#fdeaea",
      }
    }),
  };

  const [selectAll, setSelectAll] = useState(false);
  const [pendingFocusId, setPendingFocusId] = useState(null);
  const [highlightTaskId, setHighlightTaskId] = useState(null);
  const { addNotification } = useContext(NotificationsContext);
  const setNowTick = useState(0)[1];
  const [showRemarksModal, setShowRemarksModal] = useState(false);
  const [taskRemarks, setTaskRemarks] = useState([]);

  const [deleteLoading, setDeleteLoading] = useState(false);
  const statusLabelLookup = useMemo(() => {
    const map = new Map();
    statusSummary.forEach((card) => {
      map.set(card.label.toLowerCase(), card.label);
    });
    return map;
  }, []);

  const resolveStatusLabel = useCallback(
    (value) => {
      const fallback = (value || "Pending").toString().trim();
      if (!fallback) {
        return "Pending";
      }
      const match = statusLabelLookup.get(fallback.toLowerCase());
      return match || fallback;
    },
    [statusLabelLookup]
  );

  const resolveOverdueMinutes = useCallback(
    (task) => {
      if (!task) return null;
      const { normalized } = resolveAggregatedDisplayStatus(
        task,
        resolveStatusLabel
      );
      const completionTimestamp =
        normalized === "completed"
          ? resolveTaskCompletionTimestamp(task)
          : null;
      return calculateOverdueMinutes(
        resolveTaskTargetDate(task),
        completionTimestamp
      );
    },
    [resolveStatusLabel]
  );

  const resolveOverdueDisplayString = useCallback(
    (task) => {
      if (!task) return "";
      const { normalized } = resolveAggregatedDisplayStatus(
        task,
        resolveStatusLabel
      );
      const completionTimestamp =
        normalized === "completed"
          ? resolveTaskCompletionTimestamp(task)
          : null;
      return calculateOverdueDisplay(
        resolveTaskTargetDate(task),
        completionTimestamp
      );
    },
    [resolveStatusLabel]
  );



  useEffect(() => {
    let cancelled = false;

    const fetchUser = async () => {
      try {
        const res = await fetch(getApiUrl("api/auth/me"), {
          credentials: "include",
        });
        if (!res.ok) return;
        const data = await res.json();
        const resolvedId =
          data.user?.userId ||
          data.user?.id ||
          data.userId ||
          data.id ||
          data.user?.sub;
        if (resolvedId && !cancelled) {
          setUserId(resolvedId);
        }
      } catch (err) {
        console.error("Error fetching user:", err);
      }
    };

    const fetchEmployees = async () => {
      try {
        const res = await fetch(getApiUrl("api/auth/employees"));
        if (!res.ok) throw new Error("Failed to load employees");
        const data = await res.json();
        if (!cancelled) {
          setEmployees(data);
          setAllEmployees(data);
          setFilteredEmployees(data);
        }
      } catch (err) {
        console.error("Error fetching employees:", err);
        if (!cancelled) {
          setEmployees([]);
          setAllEmployees([]);
          setFilteredEmployees([]);
        }
      }
    };

    const fetchDepartments = async () => {
      try {
        const res = await fetch(getApiUrl("api/auth/departments"));
        if (!res.ok) throw new Error("Failed to load departments");
        const data = await res.json();
        if (!cancelled) {
          setDepartments(data);
        }
      } catch (err) {
        console.error("Error fetching departments:", err);
        if (!cancelled) {
          setDepartments([]);
        }
      }
    };

    const fetchProjects = async () => {
      try {
        const res = await fetch(getApiUrl("api/projects"));
        if (!res.ok) throw new Error("Failed to load projects");
        const data = await res.json();
        if (!cancelled) {
          setProjects(data);
        }
      } catch (err) {
        console.error("Error fetching projects:", err);
        if (!cancelled) {
          setProjects([]);
        }
      }
    };

    fetchUser();
    fetchEmployees();
    fetchDepartments();
    fetchProjects();

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const storedFocusId = sessionStorage.getItem("taskboard:focusTaskId");
    if (storedFocusId) {
      setPendingFocusId(storedFocusId);
      sessionStorage.removeItem("taskboard:focusTaskId");
    }
  }, []);

  useEffect(() => {
    if (!pendingFocusId || !Array.isArray(tasks) || tasks.length === 0) {
      return;
    }

    const targetId = String(pendingFocusId);
    const match = tasks.find((task) => {
      if (!task) return false;
      const idCandidates = [task.id, task.representativeTaskId];
      if (Array.isArray(task.allTaskIds)) {
        idCandidates.push(...task.allTaskIds);
      }
      return idCandidates.some((candidate) => String(candidate) === targetId);
    });

    if (match) {
      const resolvedId = match.id ?? match.representativeTaskId ?? pendingFocusId;
      setHighlightTaskId(String(resolvedId));
      setPendingFocusId(null);
    }
  }, [pendingFocusId, tasks]);

  useEffect(() => {
    if (!highlightTaskId) return;
    if (typeof window === "undefined" || typeof document === "undefined") {
      return;
    }

    const attemptFocus = () => {
      const selector = `[data-task-row="${highlightTaskId}"]`;
      const row = document.querySelector(selector);
      if (row && typeof row.scrollIntoView === "function") {
        row.scrollIntoView({ behavior: "smooth", block: "center" });
        return true;
      }
      return false;
    };

    const immediateSuccess = attemptFocus();
    let retryTimer = null;

    if (!immediateSuccess) {
      retryTimer = window.setTimeout(attemptFocus, 180);
    }

    const clearHighlightTimer = window.setTimeout(() => {
      setHighlightTaskId(null);
    }, 3500);

    return () => {
      if (retryTimer) {
        window.clearTimeout(retryTimer);
      }
      window.clearTimeout(clearHighlightTimer);
    };
  }, [highlightTaskId, filteredTasks]);

  // Live ageing ticker: re-render every second so seconds/minutes update
  useEffect(() => {
    const id = setInterval(() => setNowTick((t) => (t + 1) % 1_000_000), 1000);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    if (!userId) {
      setTasks([]);
      setFilteredTasks([]);
      return;
    }

    let cancelled = false;

    const loadTasks = async () => {
      if (!userId) return;
      setIsTaskReloading(true);
      try {
        const params = new URLSearchParams();
        params.append("page", currentPage);
        params.append("limit", pageLimit);

        if (appliedFilters.search) params.append("search", appliedFilters.search);

        // Use appliedFilters for other fields
        if (appliedFilters.department) params.append("department_id", appliedFilters.department);
        if (appliedFilters.project) params.append("project_id", appliedFilters.project);
        if (appliedFilters.employee) params.append("assigned_to", appliedFilters.employee);
        if (appliedFilters.priority) params.append("priority", appliedFilters.priority);

        // Use selectedStatus directly for immediate tab filtering
        if (selectedStatus !== "All") {
          params.append("status", selectedStatus);
        }

        // Handle overdue separately
        if (appliedFilters.overdue) {
          params.append("overdue", appliedFilters.overdue);
        }

        const res = await fetch(getApiUrl(`api/task/user/${userId}?${params.toString()}`), {
          credentials: "include",
        });

        if (!res.ok) throw new Error("Failed to fetch tasks");

        const data = await res.json();

        if (cancelled) return;

        let tasksArr = [];
        if (Array.isArray(data)) {
          tasksArr = data;
        } else if (data && Array.isArray(data.tasks)) {
          tasksArr = data.tasks;
          if (data.pagination) {
            setTotalTasks(data.pagination.total || 0);
            setTotalPages(data.pagination.totalPages || 1);
          }
          if (data.statusCounts) {
            setStatusCounts(data.statusCounts);
          }
        }

        // Client-side delegation filtering (if backend doesn't handle it fully yet, but usually backend displayMyTask handles it)
        // For deputed tasks (tasks given BY user), the endpoint is usually distinct. 
        // Assuming the backend endpoint filters correctly for "Given By Me".

        // Initial sorting by target end date ascending
        tasksArr.sort((a, b) => {
          const dateA = normalizeToDate(resolveTaskTargetDate(a));
          const dateB = normalizeToDate(resolveTaskTargetDate(b));
          if (!dateA && !dateB) return 0;
          if (!dateA) return 1;
          if (!dateB) return -1;
          return dateA - dateB;
        });

        const aggregatedTasks = aggregateTasksByGroup(tasksArr);
        setTasks(aggregatedTasks);
        setFilteredTasks(aggregatedTasks); // We can remove filteredTasks usages if we trust backend fully, but keeping for compatibility

      } catch (err) {
        console.error("Error fetching tasks:", err);
        if (!cancelled) {
          setTasks([]);
          setFilteredTasks([]);
        }
      } finally {
        if (!cancelled) setIsTaskReloading(false);
      }
    };

    loadTasks();

    return () => {
      cancelled = true;
    };
  }, [userId, showSuccess, currentPage, pageLimit, appliedFilters, selectedStatus]);

  // Filter employees based on selected department
  useEffect(() => {
    if (selectedDepartment) {
      const filtered = allEmployees.filter(
        (emp) =>
          emp.department_id &&
          emp.department_id.toString() === selectedDepartment.toString()
      );
      setFilteredEmployees(filtered);
      setSelectedEmployee(""); // Reset employee selection when department changes
    } else {
      setFilteredEmployees(allEmployees);
      setSelectedEmployee("");
    }
  }, [selectedDepartment, allEmployees]);

  // Filter tasks based on selected status, department and employee
  // Client-side filtering effects removed as we now use server-side filtering
  // keeping empty effect or removing completely if safe. 
  // Since we updated the main loadTasks effect to include all dependencies, 
  // we can remove the old client-side filtering effects.



  // Keep selection in sync with filtered view
  useEffect(() => {
    // Remove selections not present in current filteredTasks
    const filteredIds = new Set(filteredTasks.map((t) => t.id));
    const nextSelected = selectedTasks.filter((id) => filteredIds.has(id));
    if (nextSelected.length !== selectedTasks.length)
      setSelectedTasks(nextSelected);
    setSelectAll(
      nextSelected.length > 0 &&
      nextSelected.length === filteredTasks.length &&
      filteredTasks.length > 0
    );
  }, [filteredTasks, selectedTasks]);

  // Selection handlers
  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedTasks([]);
      setSelectAll(false);
    } else {
      setSelectedTasks(filteredTasks.map((t) => t.id));
      setSelectAll(true);
    }
  };

  const handleTaskSelect = (taskId) => {
    if (selectedTasks.includes(taskId)) {
      const next = selectedTasks.filter((id) => id !== taskId);
      setSelectedTasks(next);
      setSelectAll(false);
    } else {
      const next = [...selectedTasks, taskId];
      setSelectedTasks(next);
      if (next.length === filteredTasks.length) setSelectAll(true);
    }
  };

  const getExportData = () => {
    if (selectedTasks.length > 0) {
      const setIds = new Set(selectedTasks);
      return filteredTasks.filter((t) => setIds.has(t.id));
    }
    return filteredTasks;
  };

  const handleSearch = () => {
    // The filtering is already handled by useEffect above
    // This function can be used for additional search logic if needed
  };

  const handleReset = () => {
    setSearchQuery("");
    setSelectedDepartment("");
    setSelectedProject("");
    setSelectedEmployee("");
    setSelectedOverdueRange("");
    setSelectedPriority("");
    setFilteredEmployees(allEmployees);
    setFilteredTasks(tasks);

    // Explicitly reset applied filters to ensure search and other filters are cleared immediately
    setAppliedFilters({
      department: "",
      project: "",
      employee: "",
      priority: "",
      status: "All",
      overdue: "",
      search: ""
    });
    setCurrentPage(1);
  };

  // Handle action button click
  const handleActionClick = (task) => {
    const normalizedTask = {
      ...task,
      canEdit: typeof task?.canEdit === "boolean" ? task.canEdit : true,
      canDelete: typeof task?.canDelete === "boolean" ? task.canDelete : true,
    };
    setActionTask(normalizedTask);
    setShowActionModal(true);
  };

  // Handle edit task
  const handleEditTask = () => {
    setEditTask(actionTask);
    setShowEditModal(true);
    setShowActionModal(false);
    setActionTask(null);
  };

  // Handle view remarks
  const handleViewRemarks = async () => {
    if (!actionTask) return;

    try {
      // Fetch task details including remarks
      const response = await fetch(getApiUrl(`api/task/${actionTask.id}`), {
        credentials: "include",
      });

      if (response.ok) {
        const taskData = await response.json();

        const actions = Array.isArray(taskData.actions)
          ? taskData.actions
          : [];

        const remarks = actions.map((action, idx) => {
          const attachments = Array.isArray(action.attachments)
            ? action.attachments.map((file, fileIdx) => ({
              id: file.id || `${action.id || idx}-att-${fileIdx}`,
              name: file.originalName || file.fileName || `Attachment ${fileIdx + 1}`,
              size: file.fileSize,
              url: file.fileName
                ? getApiUrl(`uploads/${encodeURIComponent(file.fileName)}`)
                : null,
            }))
            : [];

          return {
            id: action.id || idx,
            status: action.status || taskData.status,
            remark: action.remark || "",
            createdAt: action.createdAt || new Date().toISOString(),
            createdBy: action.createdBy || "System",
            attachments,
          };
        });

        if (remarks.length === 0) {
          if (taskData.statusRemark && taskData.statusRemark.trim()) {
            remarks.push({
              id: 1,
              status: taskData.status,
              remark: taskData.statusRemark,
              createdAt: new Date().toISOString(),
              createdBy: taskData.FirstName
                ? `${taskData.FirstName} ${taskData.LastName}`
                : "System",
              attachments: [],
            });
          } else {
            remarks.push({
              id: 1,
              status: taskData.status,
              remark: "No remarks available for this task.",
              createdAt: new Date().toISOString(),
              createdBy: "System",
              attachments: [],
            });
          }
        }

        setTaskRemarks(remarks);
        setShowRemarksModal(true);
        setShowActionModal(false);
      } else {
        throw new Error("Failed to fetch task details");
      }
    } catch (error) {
      console.error("Error fetching remarks:", error);
      // Show error message if fetch fails
      setTaskRemarks([
        {
          id: 1,
          status: actionTask.status,
          remark:
            "Unable to load remarks. Please check your connection and try again.",
          createdAt: new Date().toISOString(),
          createdBy: "System",
          attachments: [],
        },
      ]);
      setShowRemarksModal(true);
      setShowActionModal(false);
    }
  };

  // Handle delete task
  const handleDeleteTask = async () => {
    if (!actionTask) return;

    setDeleteLoading(true);

    try {
      const deletedTaskTitle = actionTask?.title;
      const res = await fetch(getApiUrl(`api/task/${actionTask.id}`), {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          currentUserId: userId,
        }),
        credentials: "include",
      });

      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || data.message || "Failed to delete task.");
      }

      toast.success("Task deleted successfully!");
      setShowActionModal(false);
      setActionTask(null);

      // Refresh tasks list
      setTasks(prev => prev.filter((task) => task.id !== actionTask.id));
      setFilteredTasks(prev =>
        Array.isArray(prev) ? prev.filter((task) => task.id !== actionTask.id) : prev
      );

    } catch (err) {
      toast.error(err.message || "Failed to delete task.");
    } finally {
      setDeleteLoading(false);
    }
  };

  // Handle task updated
  const handleTaskUpdated = (updatedTaskData) => {
    toast.success("Task updated successfully!");

    // Optimistic Update if data is provided
    if (updatedTaskData && updatedTaskData.status && updatedTaskData.oldStatus) {
      setStatusCounts(prev => {
        const newCounts = { ...prev };
        const oldS = resolveStatusLabel(updatedTaskData.oldStatus);
        const newS = resolveStatusLabel(updatedTaskData.status);
        if (oldS !== newS) {
          newCounts[oldS] = Math.max(0, (newCounts[oldS] || 0) - 1);
          newCounts[newS] = (newCounts[newS] || 0) + 1;
        }
        return newCounts;
      });
    }

    // Refresh tasks list
    fetch(getApiUrl(`api/task/user/${userId}`), { credentials: "include" })
      .then((res) => res.json())
      .then((data) => {
        let tasksArr = [];
        let counts = { All: 0 };

        if (Array.isArray(data)) {
          tasksArr = data;
        } else if (data && Array.isArray(data.tasks)) {
          tasksArr = data.tasks;
          counts = data.statusCounts || { All: 0 };
        }

        // Update counts from server to be sure
        if (data.statusCounts) {
          setStatusCounts(data.statusCounts);
        }

        // Sort by resolved target date ascending (soonest first)
        tasksArr.sort((a, b) => {
          const dateA = normalizeToDate(resolveTaskTargetDate(a));
          const dateB = normalizeToDate(resolveTaskTargetDate(b));
          if (!dateA && !dateB) return 0;
          if (!dateA) return 1;
          if (!dateB) return -1;
          return dateA - dateB;
        });
        const aggregatedTasks = aggregateTasksByGroup(tasksArr);
        setTasks(aggregatedTasks);
        setFilteredTasks(aggregatedTasks);
      })
      .catch((err) => {
        console.error("Error refreshing tasks:", err);
      });
  };

  const handleBulkUploadSuccess = () => {
    toast.success("Bulk upload completed successfully!");
    // Refresh tasks list
    if (userId) {
      fetch(getApiUrl(`api/task/user/${userId}`), { credentials: "include" })
        .then((res) => res.json())
        .then((data) => {
          let tasksArr = [];
          if (Array.isArray(data)) {
            tasksArr = data;
          } else if (data && Array.isArray(data.tasks)) {
            tasksArr = data.tasks;
          }
          // Sort by resolved target date ascending (soonest first)
          tasksArr.sort((a, b) => {
            const dateA = normalizeToDate(resolveTaskTargetDate(a));
            const dateB = normalizeToDate(resolveTaskTargetDate(b));
            if (!dateA && !dateB) return 0;
            if (!dateA) return 1;
            if (!dateB) return -1;
            return dateA - dateB;
          });
          const aggregatedTasks = aggregateTasksByGroup(tasksArr);
          setTasks(aggregatedTasks);
          setFilteredTasks(aggregatedTasks);
        })
        .catch(() => setTasks([]));
    }
  };

  // Export helpers
  const exportToCSV = (data, filename) => {
    if (!data || data.length === 0) {
      alert("No data to export!");
      return;
    }
    const headers = [
      "S. No.",
      "Task Name",
      "Target End Date",
      "Priority",
      "Over Due",
      "Status",
      "Given To Name",
      "Given To Email",
    ];

    const rows = data.map((task, idx) => [
      idx + 1,
      task.title || "",
      task.due_date ? new Date(task.due_date).toLocaleDateString() : "",
      task.priority || "",
      resolveOverdueDisplayString(task),
      task.status || "",
      (task.displayGivenToName ||
        `${task.FirstName || ""} ${task.LastName || ""}`.trim()),
      task.displayGivenToEmail || task.email || "",
    ]);

    const csv = [
      headers.join(","),
      ...rows.map((row) =>
        row.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(",")
      ),
    ].join("\n");

    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${filename}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  const exportToExcel = async (data, filename) => {
    if (!data || data.length === 0) {
      alert("No data to export!");
      return;
    }
    try {
      const XLSX = await import("xlsx");
      const sheetData = data.map((task, idx) => ({
        "S. No.": idx + 1,
        "Task Name": task.title || "",
        "Target End Date": task.due_date
          ? new Date(task.due_date).toLocaleDateString()
          : "",
        Priority: task.priority || "",
        "Over Due": resolveOverdueDisplayString(task),
        Status: task.status || "",
        "Given To Name":
          task.displayGivenToName ||
          `${task.FirstName || ""} ${task.LastName || ""}`.trim(),
        "Given To Email": task.displayGivenToEmail || task.email || "",
      }));
      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.json_to_sheet(sheetData);
      const colWidths = Object.keys(sheetData[0]).map((key) => ({
        wch: Math.max(
          key.length,
          ...sheetData.map((r) => String(r[key]).length)
        ),
      }));
      ws["!cols"] = colWidths;
      XLSX.utils.book_append_sheet(wb, ws, "Deputed Tasks");
      XLSX.writeFile(wb, `${filename}.xlsx`);
    } catch (e) {
      console.error("Excel export failed", e);
      toast.error("Excel export failed. Please try CSV instead.");
    }
  };

  const handleExport = async (format) => {
    setExportLoading(true);
    try {
      const dataToExport = getExportData();
      if (!dataToExport || dataToExport.length === 0) {
        toast.error("No data to export for current selection/filters!");
        return;
      }
      let filename = `DeputedTasks_${selectedTasks.length > 0 ? "Selected" : selectedStatus
        }_${new Date().toISOString().split("T")[0]}`;
      if (format === "csv") exportToCSV(dataToExport, filename);
      if (format === "excel") await exportToExcel(dataToExport, filename);
      setShowExportModal(false);
      toast.success(
        `Exported ${dataToExport.length} task(s) as ${format.toUpperCase()}`
      );
    } finally {
      setExportLoading(false);
    }
  };

  return (
    <DashboardLayout
      modalOpen={
        showAddTask || showTransferTask || showEditModal || showBulkUpload
      }
    >
      <AddTaskModal
        open={showAddTask}
        onClose={() => setShowAddTask(false)}
        onTaskCreated={() => {
          showSuccessOnce("Task created successfully!");
        }}
      />
      <TransferTaskModal
        open={showTransferTask}
        onClose={() => setShowTransferTask(false)}
      />
      <EditTaskModal
        open={showEditModal}
        onClose={() => setShowEditModal(false)}
        onTaskUpdated={handleTaskUpdated}
        task={editTask}
        employees={employees}
      />
      <BulkUploadModal
        open={showBulkUpload}
        onClose={() => setShowBulkUpload(false)}
        onBulkUploadSuccess={handleBulkUploadSuccess}
      />

      <TaskDetailsModal
        open={showDetails}
        onClose={() => setShowDetails(false)}
        task={selectedTask}
      />

      {/* Action Modal */}
      {showActionModal && actionTask && (
        <div className="action-modal-overlay">
          <div className="action-modal">
            <div className="action-modal-header">
              <h3>Task Actions</h3>
              <button
                className="action-modal-close"
                onClick={() => setShowActionModal(false)}
              >
                ×
              </button>
            </div>
            <div className="action-modal-content">
              <p>
                <strong>Task:</strong> {actionTask.title}
              </p>
              <p>
                <strong>Status:</strong> {actionTask.status}
              </p>
            </div>
            <div className="action-modal-buttons">
              <button
                className="action-edit-btn"
                onClick={handleEditTask}
                disabled={actionTask?.canEdit === false}
                title={
                  actionTask?.canEdit === false ? "Edit permission denied" : ""
                }
              >
                ✏️ Edit Task
              </button>
              <button
                className="action-remarks-btn"
                onClick={handleViewRemarks}
              >
                📝 View Remarks
              </button>
              <button
                className="action-delete-btn"
                onClick={handleDeleteTask}
                disabled={actionTask?.canDelete === false || deleteLoading}
                title={
                  actionTask?.canDelete === false
                    ? "Delete permission denied"
                    : ""
                }
              >
                {deleteLoading ? (
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "8px" }}>
                    <div className="table-loader" style={{ width: "14px", height: "14px", borderWidth: "2px" }} />
                    Deleting...
                  </div>
                ) : (
                  <>🗑️ Delete Task</>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Remarks Modal */}
      {showRemarksModal && (
        <div className="action-modal-overlay">
          <div className="action-modal">
            <div className="action-modal-header">
              <h3>Task Remarks</h3>
              <button
                className="action-modal-close"
                onClick={() => setShowRemarksModal(false)}
              >
                ×
              </button>
            </div>
            <div className="action-modal-content">
              <p>
                <strong>Task:</strong> {actionTask?.title}
              </p>
              <p>
                <strong>Current Status:</strong> {actionTask?.status}
              </p>
            </div>
            <div
              className="remarks-content"
              style={{
                maxHeight: "400px",
                overflowY: "auto",
                marginBottom: "20px",
              }}
            >
              {taskRemarks.length > 0 ? (
                taskRemarks.map((remark, index) => (
                  <div
                    key={remark.id || index}
                    style={{
                      border: "1px solid #e0e0e0",
                      borderRadius: "8px",
                      padding: "15px",
                      marginBottom: "10px",
                      background: "#f9f9f9",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        marginBottom: "8px",
                      }}
                    >
                      <span
                        style={{
                          fontWeight: "bold",
                          color: "#333",
                          padding: "4px 8px",
                          borderRadius: "4px",
                          background:
                            remark.status === "Completed"
                              ? "#e8f5e8"
                              : remark.status === "Hold"
                                ? "#fff3cd"
                                : remark.status === "Revalidate"
                                  ? "#f8d7da"
                                  : remark.status === "WIP"
                                    ? "#d1ecf1"
                                    : "#e2e3e5",
                        }}
                      >
                        {remark.status}
                      </span>
                      <span style={{ fontSize: "0.85rem", color: "#666" }}>
                        {new Date(remark.createdAt).toLocaleString()}
                      </span>
                    </div>
                    <div
                      style={{
                        color: "#555",
                        lineHeight: "1.4",
                        background: "#fff",
                        padding: "10px",
                        borderRadius: "4px",
                        border: "1px solid #e9ecef",
                        minHeight: "40px",
                      }}
                    >
                      {remark.remark}
                    </div>
                    {Array.isArray(remark.attachments) &&
                      remark.attachments.length > 0 && (
                        <div
                          style={{
                            marginTop: "10px",
                            display: "flex",
                            flexDirection: "column",
                            gap: 6,
                          }}
                        >
                          <span
                            style={{
                              fontSize: "0.82rem",
                              fontWeight: 600,
                              color: "#333",
                            }}
                          >
                            Attachments
                          </span>
                          <div
                            style={{
                              display: "flex",
                              flexDirection: "column",
                              gap: 6,
                            }}
                          >
                            {remark.attachments.map((file, index) => {
                              const attachmentKey =
                                file.id || `${remark.id}-att-${index}`;
                              const content = (
                                <>
                                  <span>{file.name}</span>
                                  {file.size ? (
                                    <span
                                      style={{
                                        color: "#666",
                                        fontSize: "0.75rem",
                                      }}
                                    >
                                      {formatFileSize(file.size)}
                                    </span>
                                  ) : null}
                                </>
                              );

                              return file.url ? (
                                <a
                                  key={attachmentKey}
                                  href={file.url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 8,
                                    color: "#1a73e8",
                                    textDecoration: "none",
                                    fontSize: "0.85rem",
                                  }}
                                >
                                  {content}
                                </a>
                              ) : (
                                <div
                                  key={attachmentKey}
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 8,
                                    color: "#555",
                                    fontSize: "0.85rem",
                                  }}
                                >
                                  {content}
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    <div
                      style={{
                        fontSize: "0.8rem",
                        color: "#888",
                        marginTop: "8px",
                      }}
                    >
                      <strong>By:</strong> {remark.createdBy}
                    </div>
                  </div>
                ))
              ) : (
                <div
                  style={{
                    textAlign: "center",
                    color: "#666",
                    padding: "20px",
                  }}
                >
                  No remarks available for this task.
                </div>
              )}
            </div>
            <div className="action-modal-buttons">
              <button
                className="action-close-btn"
                onClick={() => setShowRemarksModal(false)}
                style={{
                  background: "#6c757d",
                  color: "white",
                  border: "none",
                  padding: "10px 20px",
                  borderRadius: "5px",
                  cursor: "pointer",
                }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="task-page-container">
        {/* Move filters and action buttons above the cards */}
        <div className="task-page-filters">
          <div className="filters-header">
            <h3 className="filters-heading">Deputed Tasks</h3>

            <div className="actions-right">
              <button
                className="taskboard-btn"
                onClick={() => setShowExportModal(true)}
              >
                <FaFileExport style={{ marginRight: "6px" }} /> Export
              </button>
              <button
                className="taskboard-btn"
                onClick={() => setShowBulkUpload(true)}
              >
                <FaCloudUploadAlt style={{ marginRight: "6px" }} /> Bulk Upload
              </button>
              <button
                className="taskboard-btn"
                style={{ display: "none" }}
                onClick={() => setShowTransferTask(true)}
              >
                <FaExchangeAlt style={{ marginRight: "6px" }} /> Transfer
              </button>
              <button
                className="taskboard-btn"
                onClick={() => setShowAddTask(true)}
              >
                <FaPlus style={{ marginRight: "6px" }} /> Create Task
              </button>


            </div>
          </div>

          <div className="filters-row">
            <div className="filter-group">
              <div className="input-group">
                <FaBuilding className="input-icon" />
                <Select
                  styles={reactSelectStyles}
                  value={departmentOptions.find(opt => String(opt.value) === String(selectedDepartment))}
                  onChange={(opt) => setSelectedDepartment(opt ? opt.value : "")}
                  options={departmentOptions}
                  isSearchable={true}
                  placeholder="All Departments"
                  className="task-select-container"
                  menuPortalTarget={document.body}
                  menuPlacement="auto"
                />
              </div>

              <div className="input-group">
                <FaProjectDiagram className="input-icon" />
                <Select
                  styles={reactSelectStyles}
                  value={projectOptions.find(opt => String(opt.value) === String(selectedProject))}
                  onChange={(opt) => setSelectedProject(opt ? opt.value : "")}
                  options={projectOptions}
                  isSearchable={true}
                  placeholder="All Projects"
                  className="task-select-container"
                  menuPortalTarget={document.body}
                  menuPlacement="auto"
                />
              </div>

              <div className="input-group">
                <FaUser className="input-icon" />
                <Select
                  styles={reactSelectStyles}
                  value={employeeOptions.find(opt => String(opt.value) === String(selectedEmployee))}
                  onChange={(opt) => setSelectedEmployee(opt ? opt.value : "")}
                  options={employeeOptions}
                  isSearchable={true}
                  placeholder="All Employees"
                  className="task-select-container"
                  menuPortalTarget={document.body}
                  menuPlacement="auto"
                />
              </div>

              <div className="input-group">
                <FaClock className="input-icon" />
                <Select
                  styles={reactSelectStyles}
                  value={overdueOptions.find(opt => String(opt.value) === String(selectedOverdueRange))}
                  onChange={(opt) => setSelectedOverdueRange(opt ? opt.value : "")}
                  options={overdueOptions}
                  isSearchable={false}
                  placeholder="Over Due (All)"
                  className="task-select-container"
                  menuPortalTarget={document.body}
                  menuPlacement="auto"
                />
              </div>

              <div className="input-group">
                <FaFlag className="input-icon" />
                <Select
                  styles={reactSelectStyles}
                  value={priorityOptions.find(opt => String(opt.value) === String(selectedPriority))}
                  onChange={(opt) => setSelectedPriority(opt ? opt.value : "")}
                  options={priorityOptions}
                  isSearchable={false}
                  placeholder="Priority (All)"
                  className="task-select-container"
                  menuPortalTarget={document.body}
                  menuPlacement="auto"
                />
              </div>
            </div>
          </div>

          <div className="search-cont">
            <div
              className="taskboard-search-wrapper"
              style={{
                position: "relative",
                display: "flex",
                alignItems: "center",
                flex: 1,
                maxWidth: "400px"
              }}
            >
              <input
                className="taskboard-search"
                type="text"
                placeholder="Search tasks by name..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                style={{
                  width: "100%",
                  padding: "10px 12px",
                  paddingRight: searchQuery ? "40px" : "12px",
                  border: "1px solid #ddd",
                  borderRadius: "8px",
                  fontSize: "0.95rem",
                  outline: "none",
                  transition: "all 0.2s",
                  boxShadow: "0 2px 4px rgba(0,0,0,0.02)"
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = "#7b6ef6";
                  e.target.style.boxShadow = "0 0 0 2px rgba(123, 110, 246, 0.1)";
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = "#ddd";
                  e.target.style.boxShadow = "none";
                }}
              />

              {/* Search helper tooltip */}
              {searchQuery && searchQuery !== appliedFilters.search && (
                <div style={{
                  position: "absolute",
                  top: "100%",
                  left: 0,
                  marginTop: "8px",
                  background: "#333",
                  color: "#fff",
                  padding: "6px 10px",
                  borderRadius: "6px",
                  fontSize: "0.75rem",
                  zIndex: 100,
                  pointerEvents: "none",
                  whiteSpace: "nowrap",
                  boxShadow: "0 4px 6px rgba(0,0,0,0.1)",
                  animation: "fadeIn 0.2s ease-in-out"
                }}>
                  Click <b>Apply</b> to search
                  <div style={{
                    position: "absolute",
                    top: "-4px",
                    left: "20px",
                    width: "8px",
                    height: "8px",
                    background: "#333",
                    transform: "rotate(45deg)"
                  }}></div>
                </div>
              )}
              <FaSearch
                style={{
                  position: "absolute",
                  right: searchQuery ? "35px" : "12px",
                  color: "#999",
                  pointerEvents: "none",
                }}
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery("")}
                  style={{
                    position: "absolute",
                    right: "8px",
                    background: "none",
                    border: "none",
                    color: "#999",
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    padding: "4px",
                  }}
                >
                  <span style={{ fontSize: "16px", fontWeight: "bold" }}>×</span>
                </button>
              )}
            </div>

            <div style={{ display: "flex", gap: "10px", alignItems: "center", padding: "0 2px" }}>
              <button
                className="taskboard-btn"
                onClick={handleApplyFilters}
                disabled={isTaskReloading}
                style={{ display: "flex", alignItems: "center" }}
              >
                {isTaskReloading ? (
                  <>
                    <div className="spinner-small" style={{
                      width: "16px",
                      height: "16px",
                      border: "2px solid rgba(123, 110, 246, 0.3)",
                      borderTopColor: "#7b6ef6",
                      borderRadius: "50%",
                      animation: "spin 1s linear infinite",
                      marginRight: "6px"
                    }}></div>
                    Updating...
                  </>
                ) : (
                  <>
                    <FaFilter style={{ marginRight: "6px" }} /> Apply
                  </>
                )}
              </button>

              <button
                className="taskboard-btn"
                onClick={handleReset}
              >
                <FaRedo style={{ marginRight: "6px" }} /> Reset
              </button>


            </div>
          </div>
          {/* Search Bar */}

        </div>

        {/* Export Modal */}
        {showExportModal && (
          <div
            className="export-modal-overlay"
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              width: "100vw",
              height: "100vh",
              background: "rgba(0,0,0,0.18)",
              zIndex: 999999,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
            onClick={() => setShowExportModal(false)}
          >
            <div
              className="export-modal"
              style={{
                background: "#fff",
                borderRadius: 18,
                boxShadow: "0 8px 32px rgba(60,60,120,0.18)",
                minWidth: 400,
                maxWidth: 500,
                width: "90%",
                padding: "32px 28px 28px 28px",
                position: "relative",
                animation: "fadeInModal 0.3s cubic-bezier(.4,2,.6,1)",
                display: "flex",
                flexDirection: "column",
                gap: 20,
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <div
                className="export-modal-header"
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  marginBottom: 10,
                }}
              >
                <h3
                  style={{
                    fontSize: "1.35rem",
                    fontWeight: 700,
                    color: "#22223b",
                    margin: 0,
                    letterSpacing: "-0.5px",
                  }}
                >
                  Export Deputed Task Data
                </h3>
                <button
                  className="export-modal-close"
                  style={{
                    background: "none",
                    border: "none",
                    fontSize: 22,
                    color: "#888",
                    cursor: "pointer",
                    borderRadius: "50%",
                    width: 32,
                    height: 32,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    transition: "background 0.2s",
                  }}
                  onMouseOver={(e) =>
                    (e.currentTarget.style.background = "#f2f2f2")
                  }
                  onMouseOut={(e) =>
                    (e.currentTarget.style.background = "none")
                  }
                  onClick={() => setShowExportModal(false)}
                >
                  ×
                </button>
              </div>
              <div style={{ color: "#444", fontSize: "1rem", fontWeight: 500 }}>
                {selectedTasks.length > 0
                  ? `Exporting ${selectedTasks.length} selected task(s)`
                  : `Exporting ${filteredTasks.length} task(s) in view`}
              </div>
              <div
                style={{
                  display: "flex",
                  gap: 10,
                  flexWrap: "wrap",
                  justifyContent: "center",
                }}
              >
                <button
                  onClick={() => handleExport("csv")}
                  disabled={exportLoading}
                  style={{
                    padding: "16px 20px",
                    borderRadius: 12,
                    background: "#4caf50",
                    color: "#fff",
                    border: "none",
                    cursor: exportLoading ? "not-allowed" : "pointer",
                    fontWeight: 700,
                    minWidth: 140,
                  }}
                >
                  CSV
                </button>
                <button
                  onClick={() => handleExport("excel")}
                  disabled={exportLoading}
                  style={{
                    padding: "16px 20px",
                    borderRadius: 12,
                    background: "#2196f3",
                    color: "#fff",
                    border: "none",
                    cursor: exportLoading ? "not-allowed" : "pointer",
                    fontWeight: 700,
                    minWidth: 140,
                  }}
                >
                  Excel
                </button>
              </div>
              {exportLoading && (
                <div
                  style={{
                    textAlign: "center",
                    marginTop: 20,
                    color: "#666",
                    fontWeight: 500,
                  }}
                >
                  Preparing export...
                </div>
              )}
            </div>
          </div>
        )}

        {/* Status cards are now immediately above the table */}
        {/* Status cards row styled and animated like TaskBoard/MyTask */}
        <div className="status-cards-row">
          {statusSummary.map((card) => (
            <div
              key={card.label}
              className={`status-card${selectedStatus === card.label ? " selected" : ""
                }`}
              style={{
                background: card.color,
                cursor: "pointer",
                ...(selectedStatus === card.label
                  ? { "--status-glow": card.color }
                  : {}),
              }}
              onClick={() => { setCurrentPage(1); setSelectedStatus(card.label); }}
            >
              <div className="status-label">{card.label}</div>
              <div className="status-count">
                {statusCounts[card.label] || 0}
              </div>
            </div>
          ))}
        </div>

        {/* Selection Summary */}
        {selectedTasks.length > 0 && (
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              padding: "14px 18px",
              background: "linear-gradient(0deg, #eef9f0, #eef9f0)",
              border: "1px solid #b7e1c1",
              borderRadius: "12px",
              marginTop: "8px",
              marginBottom: "12px",
              boxShadow: "0 6px 20px rgba(46, 125, 50, 0.08)",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 10,
                color: "#2e7d32",
                fontWeight: 700,
              }}
            >
              <span
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: 22,
                  height: 22,
                  borderRadius: "50%",
                  background: "#2e7d32",
                  color: "#fff",
                  fontSize: 14,
                  boxShadow: "0 2px 8px rgba(46,125,50,0.25)",
                }}
              >
                ✓
              </span>
              <span style={{ fontSize: "0.98rem" }}>
                {selectedTasks.length} task
                {selectedTasks.length !== 1 ? "s" : ""} selected
              </span>
            </div>
            <button
              onClick={() => {
                setSelectedTasks([]);
                setSelectAll(false);
              }}
              style={{
                background: "transparent",
                border: "1.5px solid #2e7d32",
                color: "#2e7d32",
                padding: "8px 14px",
                borderRadius: "999px",
                cursor: "pointer",
                fontSize: "0.85rem",
                fontWeight: 700,
                transition: "all 0.2s ease",
                letterSpacing: 0.2,
              }}
              onMouseOver={(e) => {
                e.target.style.background = "#2e7d32";
                e.target.style.color = "#fff";
                e.target.style.transform = "translateY(-1px)";
              }}
              onMouseOut={(e) => {
                e.target.style.background = "transparent";
                e.target.style.color = "#2e7d32";
                e.target.style.transform = "translateY(0)";
              }}
            >
              Clear Selection
            </button>
          </div>
        )}

        <div className="taskboard-table-wrapper" style={{ position: "relative", minHeight: "200px" }}>
          {/* Loading Overlay */}
          {isTaskReloading && (
            <div
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: "rgba(255, 255, 255, 0.7)",
                zIndex: 10,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                backdropFilter: "blur(2px)",
                borderRadius: "14px"
              }}
            >
              <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "10px" }}>
                <div className="spinner" style={{ width: "32px", height: "32px", border: "3px solid rgba(123, 110, 246, 0.3)", borderTopColor: "#7b6ef6", borderRadius: "50%", animation: "spin 1s linear infinite" }}></div>
                <span style={{ color: "#666", fontWeight: "500" }}>Loading Tasks...</span>
              </div>
            </div>
          )}
          <table className="taskboard-table">
            <thead>
              <tr>
                <th style={{ width: "50px", textAlign: "center" }}>
                  <input
                    type="checkbox"
                    checked={selectAll}
                    onChange={handleSelectAll}
                    style={{
                      width: "16px",
                      height: "16px",
                      cursor: "pointer",
                      accentColor: "#7b6ef6",
                    }}
                  />
                </th>
                <th>S. No.</th>
                <th>Task Name</th>
                <th>Target End Date</th>
                <th>Priority</th>
                <th>Over Due</th>
                <th>Status</th>
                <th>Given To</th>
                <th>View</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {isTaskReloading ? (
                <tr>
                  <td colSpan="10" style={{ textAlign: "center", padding: "24px" }}>
                    <div style={{ display: "flex", justifyContent: "center", alignItems: "center", gap: "10px" }}>
                      <div className="table-loader" />
                      <span>Loading Tasks...</span>
                    </div>
                  </td>
                </tr>
              ) : filteredTasks.length === 0 ? (
                <tr>
                  <td colSpan="10" style={{ textAlign: "center" }}>
                    No data available in table
                  </td>
                </tr>
              ) : (
                filteredTasks.map((task, idx) => {
                  const { label: displayStatus, normalized } =
                    resolveAggregatedDisplayStatus(task, resolveStatusLabel);
                  const statusBadgeKey = badgeClassLookup[normalized] || "Pending";
                  const statusTooltip = formatStatusBreakdownTooltip(
                    task,
                    resolveStatusLabel
                  );
                  const overdueDisplay = resolveOverdueDisplayString(task);

                  return (
                    <tr
                      key={task.id}
                      data-task-row={task.id}
                      className={`taskboard-row${String(task.id) === String(highlightTaskId)
                        ? " taskboard-row--highlight"
                        : ""
                        }`}
                    >
                      <td style={{ textAlign: "center" }}>
                        <input
                          type="checkbox"
                          checked={selectedTasks.includes(task.id)}
                          onChange={() => handleTaskSelect(task.id)}
                          style={{
                            width: "16px",
                            height: "16px",
                            cursor: "pointer",
                            accentColor: "#7b6ef6",
                          }}
                        />
                      </td>
                      <td>{idx + 1}</td>
                      <td>
                        <b>{(task.title || "").toUpperCase()}</b>
                      </td>
                      <td>{formatDate(task.due_date)}</td>
                      <td>{task.priority || ""}</td>
                      <td>
                        {overdueDisplay}
                      </td>
                      <td>
                        <span
                          className={`taskboard-status-badge ${statusBadgeKey}`}
                          title={statusTooltip || undefined}
                        >
                          {displayStatus}
                        </span>
                      </td>
                      <td>
                        <div>
                          {(task.displayGivenToName &&
                            task.displayGivenToName.trim()) ||
                            `${task.FirstName || ""} ${task.LastName || ""}`
                              .replace(/\s+/g, " ")
                              .trim() ||
                            "-"}
                        </div>
                        <div className="taskboard-email">
                          {task.displayGivenToEmail || task.email || ""}
                        </div>
                        {task.hasMultipleAssignees &&
                          task.assigneeSummary?.names?.length > 1 && (
                            <div
                              style={{
                                color: "#6b7280",
                                fontSize: "0.75rem",
                                marginTop: 4,
                              }}
                            >
                              {task.assigneeSummary.names.slice(0, 3).join(", ")}
                              {task.assigneeSummary.total > 3
                                ? `, +${task.assigneeSummary.total - 3
                                } more`
                                : ""}
                            </div>
                          )}
                      </td>
                      <td>
                        <button
                          className="taskboard-view-btn"
                          onClick={() => {
                            setSelectedTask(task);
                            setShowDetails(true);
                          }}
                        >
                          <FaEye />
                        </button>
                      </td>
                      <td>
                        <button
                          className="taskboard-action-btn"
                          style={{
                            background: "none",
                            border: "none",
                            borderRadius: "12px",
                            width: 42,
                            height: 37,
                            padding: 0,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            cursor: "pointer",
                            transition: "background 0.18s",
                          }}
                          onMouseOver={(e) =>
                            (e.currentTarget.style.background = "#f5f5f5")
                          }
                          onMouseOut={(e) =>
                            (e.currentTarget.style.background = "#fff")
                          }
                          onClick={() => handleActionClick(task)}
                        >
                          <FaEllipsisV size={16} color="#555" />
                        </button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div style={{ marginTop: "20px", padding: "0 2px" }}>
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            totalItems={totalTasks}
            pageSize={pageLimit}
            onPageChange={(page) => setCurrentPage(page)}
            onPageSizeChange={(limit) => {
              setPageLimit(limit);
              setCurrentPage(1);
            }}
          />
        </div>
      </div>
    </DashboardLayout >
  );
};

export default Task;
